import { createApp } from '@app/adapters/create-app'

import { SfLog } from '@app/adapters/logger'
import ReportGraphsDrawer from '@app/application/report/components/report-graphs-drawer.vue'
import getPiniaInstance from '@app/shared/pinia/instance'
import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import type { DrawerState } from '@app/application/report/types'
import type { Locale } from '@app/shared/types/locale'

const SUPPORTED_LOCALES: Locale[] = ['da', 'de', 'en', 'fi', 'fr', 'nl', 'se']

window.onDomLoad(function ($dom) {
  const drawerState = readDrawerState()
  const initialIsOpen = readInitialIsOpen()
  const initialWidthPct = readInitialWidthPct()

  $dom.find("[data-behaviour='reports/graphs-left-drawer']").each(function () {
    const data = this.dataset
    const reportTemplateId = parseInt(data.reportTemplateId ?? '0', 10)
    const locale = parseLocale(data.locale)
    const pinToTop = data.pinToTop === 'true'
    const locked = data.locked === 'true'
    const canCustomise = data.canCustomise === 'true'
    const setupLocked = data.setupLocked === 'true'
    const pinnable = data.pinnable === 'true'
    const pinLocked = data.pinLocked === 'true'

    const store = useReportGraphsDrawer(getPiniaInstance())
    store.hydrate({
      reportTemplateId,
      locale,
      state: drawerState,
      pinToTop,
      locked,
      canCustomise,
      setupLocked,
      pinnable,
      pinLocked,
      isOpen: initialIsOpen,
      widthPct: initialWidthPct,
    })

    createApp(ReportGraphsDrawer, {}).mount(this)
  })
})

function readInitialIsOpen(): boolean {
  const value =
    window?.Silverfin?.currentUserPreferences?.report_graphs_drawer_open
  return value === undefined ? true : !!value
}

function readInitialWidthPct(): number | null {
  const value =
    window?.Silverfin?.currentUserPreferences?.report_graphs_drawer_width
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function parseLocale(raw: string | undefined): Locale {
  if (raw && (SUPPORTED_LOCALES as string[]).includes(raw)) {
    return raw as Locale
  }
  return 'en'
}

function readDrawerState(): DrawerState {
  const node = document.getElementById('page-data/reports/graphs-drawer')
  if (!node?.textContent) return emptyDrawerState()
  try {
    return JSON.parse(node.textContent) as DrawerState
  } catch (error) {
    SfLog.error('Failed to parse report graphs drawer state', {
      error: error instanceof Error ? error : new Error(String(error)),
    })
    return emptyDrawerState()
  }
}

function emptyDrawerState(): DrawerState {
  return { pinned: null, highlighted: [], insight: [], resettable: false }
}
